import { useTranslation } from 'react-i18next';

import Info from '@/assets/icons/info.svg?component';
import { CardDivider } from '@/components/atoms/Card';
import { Overline } from '@/components/atoms/Eyebrow';
import SkeletonBox from '@/components/atoms/SkeletonBox/SkeletonBox';
import TitleIcon from '@/components/atoms/TitleIcon/TitleIcon';
import ContentBlock from '@/components/molecules/ContentBlock';
import { localizedDate } from '@/utils/date';
import { formatNumberByLocale, parseLocale } from '@/utils/localeFormatters';

import styles from './HealthWoundsInsightInformationPanel.module.scss';

export interface HealthWoundsInsightInformationPanelProps {
    isLoading: boolean;
    fishCount: number | null;
    lastMeasurement: string;
}

export default function HealthWoundsInsightInformationPanel(
    props: HealthWoundsInsightInformationPanelProps
) {
    const { isLoading, fishCount, lastMeasurement } = props;
    const { t, i18n } = useTranslation('healthModule');
    const locale = parseLocale(i18n.language);

    const fishCountLabel =
        fishCount != null
            ? formatNumberByLocale(fishCount, locale, 0)
            : t('dashboard.notAvailable');

    return (
        <ContentBlock
            title={t('detail.common.informationTitle')}
            titleIcon={<TitleIcon icon={Info} />}
            className={styles.infoPanel}
            spacing="none"
            shrink>
            <div className={styles.panelBody} data-testid="health-wounds-insight-information-panel">
                <div className={styles.metricBlock}>
                    <div className={styles.metricRow}>
                        <Overline as="p" className={styles.metricLabel}>
                            {t('detail.common.measuredFishCountTitle')}
                        </Overline>
                        {isLoading ? (
                            <SkeletonBox
                                width={64}
                                height={24}
                                borderRadius={8}
                                className={styles.skeleton}
                            />
                        ) : (
                            <p className={styles.metricValue}>{fishCountLabel}</p>
                        )}
                    </div>
                    <div className={styles.metricRow}>
                        <Overline as="p" className={styles.metricLabel}>
                            {t('detail.common.lastMeasurement')}
                        </Overline>
                        {isLoading ? (
                            <SkeletonBox
                                width={100}
                                height={24}
                                borderRadius={8}
                                className={styles.skeleton}
                            />
                        ) : (
                            <p className={styles.metricValue}>
                                {lastMeasurement
                                    ? localizedDate(lastMeasurement, locale)
                                    : t('dashboard.notAvailable')}
                            </p>
                        )}
                    </div>
                </div>

                <div className={styles.divider}>
                    <CardDivider />
                </div>

                {isLoading ? (
                    <SkeletonBox
                        width="100%"
                        height={120}
                        borderRadius={8}
                        className={styles.skeleton}
                    />
                ) : (
                    <div className={styles.woundTypeSection}>
                        <p className={styles.insightIntro}>{t('wounds.insightIntro')}</p>
                        <Overline as="p" className={styles.woundTypeTitle}>
                            {t('wounds.acute')}
                        </Overline>
                        <p className={styles.woundTypeDescription}>{t('wounds.acuteWoundsInfo')}</p>
                        <Overline as="p" className={styles.woundTypeTitle}>
                            {t('wounds.healing')}
                        </Overline>
                        <p className={styles.woundTypeDescription}>
                            {t('wounds.healingWoundsInfo')}
                        </p>
                    </div>
                )}
            </div>
        </ContentBlock>
    );
}
